import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Space,
  Tooltip,
  Divider,
  Input,
  DatePicker,
  Spin,
  Select,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  SettingOutlined,
  FilterOutlined,
  SortAscendingOutlined,
} from "@ant-design/icons";
import ColumnSettingModal from "./ColumnSettingModal";
import FilterModal from "./FilterModal";
import SortModal from "./SortModal";
import { MdRefresh } from "react-icons/md";
import "./Toolbar.scss";
import moment from "moment";

const Toolbar = ({
  columns,
  visibleCols,
  setVisibleCols,
  onSearch,
  onFilter,
  filters,
  sorter,
  onSort,
  onInsert,
  refetch,
  onFilterByDate,
  extraButtons = [],
  loading = false,
  translation = {},
  startDate,
  endDate,
  showFilter = true,
  sortOptions,
}) => {
  const [colModalOpen, setColModalOpen] = useState(false);
  // Trạng thái search
  const [searchValue, setSearchValue] = useState("");
  // Trạng thái filter
  const [filterOpen, setFilterOpen] = useState(false);
  // Trạng thái sort
  const [sortOpen, setSortOpen] = useState(false);

  const handleSearch = () => {
    onSearch(searchValue);
  };

  const handleFilter = (fs) => {
    onFilter(fs);
    setFilterOpen(false);
  };

  const handleSort = (s) => {
    onSort(s);
    setSortOpen(false);
  };

  const handleFilterByDate = (dates) => {
    onFilterByDate(dates);
  };

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const startDateValue = useMemo(() => {
    return startDate ? moment(startDate) : null;
  }, [startDate]);

  const endDateValue = useMemo(() => {
    return endDate ? moment(endDate) : null;
  }, [endDate]);

  return (
    <>
      <div>
      <Row gutter={[24, 24]} className="mb-3">
        {onSearch && (
          <Col xs={24} md={6} lg={6} xl={4}>
            <Input.Search
              className="w-100"
              autoFocus
              placeholder={translation("dataTable.search")}
              onChange={(e) => {
                setSearchValue(e.target.value);
              }}
              onSearch={handleSearch}
            />
          </Col>
        )}
        {columns
          .filter((col) => col.dataIndex && col.filterType && col.showOnBar)
          .map((col) => {
            const filterKey = col.filterKey || col.dataIndex;
            return (
              <Col key={col.dataIndex} xs={24} md={8} lg={6} xl={4}>
                {col.filterType === "select" && (
                  <Select
                    style={{ width: "100%" }}
                    allowClear
                    className="w-100"
                    placeholder={col.title}
                    value={filters[filterKey]}
                    onChange={(value) =>
                      onFilter({ ...filters, [filterKey]: value })
                    }
                    options={col.filterOptions}
                  />
                )}
                {col.filterType === "search" && (
                  <Input
                    className="w-100"
                    placeholder={col.title}
                    value={filters[filterKey]}
                    onChange={(e) =>
                      onFilter({ ...filters, [filterKey]: e.target.value })
                    }
                  />
                )}
                {col.filterType === "date" && (
                  <DatePicker
                    style={{ width: "100%" }}
                    allowClear
                    className="w-100"
                    placeholder={col.title}
                    value={
                      filters[filterKey]
                        ? moment(filters[filterKey], "DD/MM/YYYY")
                        : null
                    }
                    onChange={(value) =>
                      onFilter({
                        ...filters,
                        [filterKey]: moment(value).format("DD/MM/YYYY"),
                      })
                    }
                  />
                )}
              </Col>
            );
          })}
        {onFilterByDate && (
          <Col xs={24} md={8} lg={6} xl={4}>
            <DatePicker.RangePicker
              className="w-100"
              format="DD/MM/YYYY"
              onChange={handleFilterByDate}
              value={[startDateValue, endDateValue]}
              placeholder={[
                translation("dataTable.startDate"),
                translation("dataTable.endDate"),
              ]}
            />
          </Col>
        )}
        <Col xs={24} md={24} lg={18} xl={12} className="d-flex gap-4 flex-wrap">
          {showFilter && (
            <Tooltip title={translation("dataTable.filter")}>
              <Button
                onClick={() => setFilterOpen(true)}
                className="toolbar-btn"
                icon={<FilterOutlined />}
              >
                <span className="btn-text">{translation("dataTable.filter")}</span>
              </Button>
            </Tooltip>
          )}
          {extraButtons.map((button, index) => (
            <React.Fragment key={index}>{button}</React.Fragment>
          ))}
          <Tooltip title={translation("dataTable.sort")}>
            <Button
              className="icon-btn"
              icon={<SortAscendingOutlined />}
              onClick={() => setSortOpen(true)}
            >
              <span className="btn-text">{translation("dataTable.sort")}</span>
            </Button>
          </Tooltip>
          <Tooltip title={translation("dataTable.columnSetting")}>
            <Button
              className="toolbar-btn"
              icon={<SettingOutlined />}
              onClick={() => setColModalOpen(true)}
            >
              <span className="btn-text">
                {translation("dataTable.columnSetting")}
              </span>
            </Button>
          </Tooltip>
          {onInsert && (
            <Button
              type="primary"
              className="toolbar-btn"
              icon={<PlusOutlined />}
              onClick={onInsert}
            >
              {translation("dataTable.add")}
            </Button>
          )}
          <Tooltip title={translation("dataTable.refresh")}>
            <Button
              className="toolbar-btn"
              icon={loading ? <Spin /> : <MdRefresh />}
              onClick={refetch}
            />
          </Tooltip>
        </Col>
      </Row>
      </div>
      <ColumnSettingModal
        open={colModalOpen}
        columns={columns}
        visibleCols={visibleCols}
        setVisibleCols={setVisibleCols}
        onClose={() => setColModalOpen(false)}
      />
      {/* Modal lọc */}
      {showFilter && (
        <FilterModal
          open={filterOpen}
          columns={columns}
          filters={filters}
          onClose={() => setFilterOpen(false)}
          onApply={(fs) => {
            handleFilter(fs);
          }}
        />
      )}
      {/* Modal sắp xếp */}
      <SortModal
        open={sortOpen}
        columns={columns}
        sorter={sorter}
        sortOptions={sortOptions}
        onApply={(s) => {
          handleSort(s);
        }}
        onClose={() => setSortOpen(false)}
      />
    </>
  );
};

export default Toolbar;
